import { Activity, Clock3 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  formatInjuryDateLabel,
  formatInjurySide,
  formatInjurySince,
  getInjuryProgress,
  resolveInjuryStatusKey,
} from "../common/injuryUtils";

function statusTone(statusKey, isDarkMode) {
  if (statusKey === "returning_soon") {
    return isDarkMode
      ? "border-emerald-500/35 bg-emerald-500/10 text-emerald-200"
      : "border-emerald-200 bg-emerald-50/90 text-emerald-800";
  }
  if (statusKey === "ending") {
    return isDarkMode
      ? "border-sky-500/35 bg-sky-500/10 text-sky-200"
      : "border-sky-200 bg-sky-50/90 text-sky-800";
  }
  return isDarkMode
    ? "border-rose-500/30 bg-rose-500/10 text-rose-100"
    : "border-rose-200 bg-rose-50/90 text-rose-900";
}

export default function InjurySummaryPanel({ injuries = [], isDarkMode = false, compact = false }) {
  const { t, i18n } = useTranslation();

  if (!injuries.length) return null;

  return (
    <div className={`flex flex-wrap gap-2 ${compact ? "" : "mt-2"}`}>
      {injuries.map((injury) => {
        const side = formatInjurySide(injury.side, t);
        const region = injury.region || t("player_risk_alert.where_unknown", { defaultValue: "Not specified" });
        const type = injury.type || null;
        const since = formatInjurySince(injury, i18n?.language);
        const startedOn = formatInjuryDateLabel(injury);
        const statusKey = resolveInjuryStatusKey(injury);
        const { weeksRemaining, weeksElapsed } = getInjuryProgress(injury);
        const statusLabel = t(`player_risk_alert.injury_status_${statusKey}`, {
          defaultValue:
            statusKey === "returning_soon"
              ? "Return soon"
              : statusKey === "ending"
              ? "End of unavailability"
              : "In progress",
        });

        return (
          <div
            key={injury.id || `${region}-${injury.injury_date}`}
            className={`min-w-0 flex-1 rounded-lg border px-2.5 py-1.5 text-[11px] leading-snug ${statusTone(statusKey, isDarkMode)}`}
          >
            <div className="flex items-start gap-1.5">
              <Activity className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-80" strokeWidth={2.25} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold">
                  {region}
                  {side ? ` · ${side}` : ""}
                  {type ? ` · ${type}` : ""}
                </p>
                <div className={`mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 ${isDarkMode ? "text-white/75" : "text-gray-600"}`}>
                  <span className="font-medium">{statusLabel}</span>
                  {since ? (
                    <span className="inline-flex items-center gap-1">
                      <Clock3 className="h-3 w-3 opacity-70" />
                      {t("player_risk_alert.since", { defaultValue: "Since" })} {since}
                    </span>
                  ) : null}
                  {startedOn ? (
                    <span>
                      {t("player_risk_alert.injury_started", { defaultValue: "Started" })}: {startedOn}
                    </span>
                  ) : null}
                  {Number.isFinite(weeksRemaining) && weeksRemaining > 0 ? (
                    <span>
                      {t("player_risk_alert.injury_weeks_remaining", {
                        defaultValue: "{{count}} wk left",
                        count: weeksRemaining,
                      })}
                    </span>
                  ) : null}
                  {Number.isFinite(weeksElapsed) && weeksElapsed > 0 && !weeksRemaining ? (
                    <span>
                      {t("player_risk_alert.injury_weeks_elapsed", {
                        defaultValue: "{{count}} wk",
                        count: weeksElapsed,
                      })}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
